/*
 * One client, opened from the Clients table. It shows only what the partner may
 * already see: the ClientSummary fields and the commission payments that client
 * earned them (GET /commissions). Never a portfolio, holdings or the client's own
 * payments. See "Client privacy" in AGENTS.md before adding anything here.
 */

import { commissions, type ClientSummary, type Commission } from "../api/client";
import { useLoad } from "../api/useLoad";
import { COMMISSION_STATE, Dialog, LoadError, SkeletonRows, Status, accountStatus, subscriptionStatus } from "./bits";
import { fmtDate, inr, plural, rateLabel } from "./format";
import "./clients.css";

/**
 * The commissions that belong to this client. The API names the client on each
 * commission but, until it also sends `referral_id`, a name is all there is to
 * match on, so a name shared by two clients is not guessed at: those show none.
 */
export function commissionsFor(all: Commission[], c: ClientSummary, nameIsUnique: boolean): Commission[] {
  if (all.some((x) => x.referral_id)) return all.filter((x) => x.referral_id === c.referral_id);
  return nameIsUnique ? all.filter((x) => x.client_name === c.client_name) : [];
}

export default function ClientSheet({
  c,
  nameIsUnique,
  onClose,
}: {
  c: ClientSummary;
  nameIsUnique: boolean;
  onClose: () => void;
}) {
  const list = useLoad(() => commissions.list(), []);
  const acct = accountStatus(c.account_status);
  const sub = subscriptionStatus(c.subscription_status);
  const rows = list.data ? commissionsFor(list.data.items, c, nameIsUnique) : [];
  const active = c.subscription_status === "active" || c.subscription_status === "trialing";

  return (
    <Dialog
      variant="sheet"
      title={c.client_name}
      description={`Client since ${fmtDate(c.client_since)}`}
      onClose={onClose}
    >
      <dl className="kv client-kv">
        <div>
          <dt>Account</dt>
          <dd>
            <Status tone={acct.tone}>{acct.label}</Status>
          </dd>
        </div>
        <div>
          <dt>Plan</dt>
          <dd>
            <span className={`chip${c.plan_name ? " is-gold" : ""}`}>{c.plan_name ?? "No plan yet"}</span>
          </dd>
        </div>
        <div>
          <dt>Subscription</dt>
          <dd>
            <Status tone={sub.tone}>{sub.label}</Status>
            {c.subscribed_until && (
              <small>
                {active ? "Until" : "Ended"} {fmtDate(c.subscribed_until)}
              </small>
            )}
          </dd>
        </div>
        <div>
          <dt>Commission earned</dt>
          <dd>
            <strong>{inr(c.commission_total)}</strong>
            {Number(c.commission_in_progress) > 0 && <small>{inr(c.commission_in_progress)} in progress</small>}
          </dd>
        </div>
      </dl>

      <h3 className="client-sheet__heading">Commission payments</h3>
      {list.error ? (
        <LoadError message={list.error} onRetry={list.reload} />
      ) : !list.data ? (
        <SkeletonRows rows={3} label="Loading commission payments" />
      ) : rows.length === 0 ? (
        <p className="client-sheet__empty">
          {Number(c.commission_total) + Number(c.commission_in_progress) > 0 && !nameIsUnique
            ? "Another client has the same name, so their payments can't be listed here yet. See Commissions for the full list."
            : "No commission yet. It starts when this client pays for a subscription."}
        </p>
      ) : (
        <>
          <p className="client-sheet__sub">{plural(rows.length, "payment")}, newest first.</p>
          {/* A list, not a table: the sheet is narrower than the table breakpoints assume. */}
          <ul className="client-pay">
            {rows.map((x) => {
              const st = COMMISSION_STATE[x.state];
              return (
                <li key={x.id} className={x.state === "reversed" ? "is-reversed" : undefined}>
                  <div>
                    <strong>{fmtDate(x.earned_at)}</strong>
                    <small>
                      {x.invoice_number ?? "No invoice"} · {inr(x.base_amount)} excl. GST at {rateLabel(x.rate)}
                    </small>
                  </div>
                  <div className="client-pay__amount">
                    <strong>{inr(x.amount)}</strong>
                    <Status tone={st.tone}>{st.label}</Status>
                    {x.state === "pending" && <small>Until {fmtDate(x.available_at)}</small>}
                  </div>
                </li>
              );
            })}
          </ul>
        </>
      )}
    </Dialog>
  );
}
